// Home page sections (hero, marquee, life story, picks, promise, trade teaser, finder): scroll-driven motion without
// GSAP. Every part only runs when its section is on the page. The blob field follows the anchors by itself.

const html = document.documentElement
const reduced = html.classList.contains('reduced')
const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v))
const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2) // power3.inOut
const easeOut = (t) => 1 - (1 - t) ** 3
const easeIn = (t) => t * t * t
const backOut = (t) => 1 + 2.6 * (t - 1) ** 3 + 1.6 * (t - 1) ** 2

// One rAF-throttled scroll loop for everything below
const onScroll = []
let ticking = false
addEventListener('scroll', () => ticking || ((ticking = true), requestAnimationFrame(() => ((ticking = false), onScroll.forEach((f) => f())))), { passive: true })

// ---------- Sections pop in when scrolled to: [data-inview] gets .in once ----------
const io = new IntersectionObserver((entries) => entries.forEach((e) => e.isIntersecting && (e.target.classList.add('in'), io.unobserve(e.target))), { rootMargin: '0px 0px -15% 0px' })
document.querySelectorAll('[data-inview]').forEach((el) => io.observe(el))

// ---------- Hero: blob sinks and shrinks as you leave, the phone lifts, a satellite drifts off; click → splash ----------
const hero = document.querySelector('[data-hero]')
if (hero && !reduced) {
  const blob = hero.querySelector('[data-hero-blob]')
  const phone = hero.querySelector('.hero__phone')
  const sat = hero.querySelector('.hero__sat--2')
  let squash = 1
  const update = () => {
    const p = clamp(scrollY / (hero.offsetHeight || 1)) // never 0/0: a NaN scale would break the blob for good
    blob.style.transform = `translateY(${38 * p}%)`
    blob.dataset.blobScale = ((1 - 0.45 * p) * squash).toFixed(3)
    if (phone) phone.style.translate = `0 ${-22 * p}%`
    if (phone) phone.style.rotate = `${-8 * p}deg`
    if (sat) sat.style.translate = `${-260 * p}% ${320 * p}%`
  }
  onScroll.push(update)
  update()

  // Splash: the blob bursts into droplets that fly out and melt back in
  const splash = () => {
    const r = blob.getBoundingClientRect()
    const cx = r.left + r.width / 2
    const cy = r.top + r.height / 2
    squash = 0.78
    update()
    setTimeout(() => ((squash = 1), update()), 160) // the engine's spring gives the rebound
    for (let i = 0; i < 14; i++) {
      const d = document.createElement('span')
      const size = 16 + Math.random() * 46
      d.className = 'drop'
      d.dataset.blob = `drop-${Date.now()}-${i}`
      Object.assign(d.style, { left: `${cx}px`, top: `${cy}px`, width: `${size}px`, height: `${size}px` })
      document.body.append(d)
      const a = (i / 14) * Math.PI * 2 + Math.random() * 0.4
      const dist = r.width * (0.45 + Math.random() * 0.45)
      const out = 550 + Math.random() * 300
      d.animate(
        [
          { transform: 'none' },
          { transform: `translate(${Math.cos(a) * dist}px, ${Math.sin(a) * dist}px)`, offset: out / (out + 1050), easing: 'cubic-bezier(0.16, 1, 0.3, 1)' },
          { transform: `translate(${Math.cos(a) * dist}px, ${Math.sin(a) * dist}px)`, offset: (out + 150) / (out + 1050), easing: 'cubic-bezier(0.87, 0, 0.13, 1)' },
          { transform: 'scale(0.2)' },
        ],
        { duration: out + 1050 }
      ).onfinish = () => d.remove()
    }
  }
  hero.querySelector('[data-splash]')?.addEventListener('click', splash)
  hero.querySelector('.hero__stage').addEventListener('click', (e) => !e.target.closest('a, button') && splash())
}

// ---------- Marquee: runs by itself, speeds up (and turns) with the scroll ----------
const tracks = [...document.querySelectorAll('[data-marquee]')]
if (tracks.length && !reduced) {
  let last = scrollY
  let boost = 0
  onScroll.push(() => {
    boost = clamp((scrollY - last) / 8, -6, 6)
    last = scrollY
  })
  const tick = () => {
    boost *= 0.92
    for (const t of tracks) {
      const a = t.getAnimations()[0]
      if (a) a.playbackRate = 1 + boost
    }
    requestAnimationFrame(tick)
  }
  tick()
}

// ---------- Life story: ten blobs re-form heart → puddle → bandage cross → phone → circle while pinned ----------
// [x, y, diameter] as fractions of the stage, relative to its centre (from the prototype)
const HEART = [[-0.17, -0.1, 0.4], [0.17, -0.1, 0.4], [-0.12, 0.1, 0.28], [0.12, 0.1, 0.28], [0, 0.14, 0.3], [0, 0.26, 0.18], [0, 0.34, 0.1], [-0.25, 0.01, 0.18], [0.25, 0.01, 0.18], [0, -0.04, 0.26]]
const PUDDLE = [[-0.36, 0.34, 0.07], [-0.26, 0.31, 0.13], [-0.14, 0.29, 0.19], [0, 0.28, 0.23], [0.14, 0.29, 0.19], [0.26, 0.31, 0.13], [0.36, 0.34, 0.07], [-0.06, 0.36, 0.1], [0.08, 0.36, 0.1], [0.2, 0.37, 0.06]]
const CROSS = [[-0.28, -0.28, 0.2], [-0.14, -0.14, 0.2], [0, 0, 0.26], [0.14, 0.14, 0.2], [0.28, 0.28, 0.2], [0.28, -0.28, 0.2], [0.14, -0.14, 0.2], [-0.14, 0.14, 0.2], [-0.28, 0.28, 0.2], [0, 0, 0.2]]
const PHONE = [[-0.1, -0.3, 0.22], [0.1, -0.3, 0.22], [-0.1, -0.15, 0.22], [0.1, -0.15, 0.22], [-0.1, 0, 0.22], [0.1, 0, 0.22], [-0.1, 0.15, 0.22], [0.1, 0.15, 0.22], [-0.1, 0.3, 0.22], [0.1, 0.3, 0.22]]
const CIRCLE = Array.from({ length: 10 }, (_, i) => [Math.cos(i) * 0.04, Math.sin(i) * 0.04, i === 0 ? 0.84 : 0.6])
// timeline in "beats" (the prototype's GSAP seconds): formation keyframes, then hold
const KEYS = [[0, HEART], [0.8, HEART], [1.6, PUDDLE], [2.4, PUDDLE], [3.2, CROSS], [4.0, CROSS], [4.8, PHONE], [5.2, PHONE], [6.0, CIRCLE]]
const TOTAL = 7

const life = document.querySelector('[data-life]')
if (life) {
  const dots = [...life.querySelectorAll('.life__dot')]
  const chs = [...life.querySelectorAll('[data-ch]')]
  const rail = [...life.querySelectorAll('.life__rail li')]
  const old = life.querySelector('.life__old')
  const neu = life.querySelector('.life__new')
  const place = (d, [x, y, w]) => Object.assign(d.style, { left: `${50 + x * 100}%`, top: `${50 + y * 100}%`, width: `${w * 100}%` })
  const at = (T, i) => {
    const t = T - i * 0.025
    for (let k = KEYS.length - 2; k >= 0; k--) {
      const [t0, a] = KEYS[k]
      const [t1, b] = KEYS[k + 1]
      if (t >= t0) {
        const u = ease(clamp((t - t0) / (t1 - t0)))
        return a[i].map((v, j) => v + (b[i][j] - v) * u)
      }
    }
    return KEYS[0][1][i]
  }
  let lav = null
  let active = -1
  const update = () => {
    const r = life.getBoundingClientRect()
    const T = clamp(-r.top / (r.height - innerHeight)) * TOTAL
    dots.forEach((d, i) => place(d, at(T, i)))
    const isLav = T > 1 && T < 2.7
    if (isLav !== lav) dots.forEach((d) => (d.dataset.blobColor = isLav ? 'lavender' : 'yellow'))
    lav = isLav
    const a = Math.min(chs.length - 1, T < 0.9 ? 0 : T < 2.5 ? 1 : T < 5.3 ? 2 : 3)
    if (a !== active) {
      chs.forEach((c, i) => (c.classList.toggle('is-on', i === a), c.classList.toggle('is-past', i < a)))
      rail.forEach((li, i) => li.classList.toggle('is-on', i <= a))
      active = a
    }
    // the old phone drops in (chapter 2) and falls through (chapter 3)
    if (old) {
      let y = -120, rot = -20, o = 0
      if (T >= 1 && T < 1.8) ((u) => ((y = -120 + 120 * u), (rot = -20 + 104 * u), (o = u)))(easeOut((T - 1) / 0.8))
      else if (T >= 1.8 && T < 2.4) (y = 0), (rot = 84), (o = 1)
      else if (T >= 2.4 && T < 3.1) ((u) => ((y = -160 * u), (rot = 84 + 116 * u), (o = 1 - u)))(easeIn((T - 2.4) / 0.7))
      Object.assign(old.style, { transform: `translateY(${y}%) rotate(${rot}deg)`, opacity: o })
    }
    // the new one arrives in the last chapter
    if (neu) {
      const u = clamp((T - 5.6) / 0.8)
      const b = backOut(u)
      Object.assign(neu.style, { transform: `translateY(${50 - 50 * b}%) scale(${0.6 + 0.4 * b}) rotate(${14 - 20 * b}deg)`, opacity: u })
    }
  }
  if (reduced) dots.forEach((d, i) => place(d, CIRCLE[i]))
  else {
    onScroll.push(update)
    update()
  }
}

// ---------- Härtetest: 22 blobs go from a lumpy used-device heap through the tests to a check mark ----------
// [x, y, diameter] as fractions of the stage, relative to its centre. Blobs overlap a lot (spacing ≈ 0.7–0.85 ×
// diameter) so the shapes stay soft. Dots 0–15 body, 16–21 inner (low goo: battery bars, pupil, tap); dot 15 is
// the one that fails.
const T_GREY = '#a8a5a5'
const along = (pts, n) => {
  // n points evenly along a polyline
  const seg = pts.slice(1).map((p, i) => Math.hypot(p[0] - pts[i][0], p[1] - pts[i][1]))
  const len = seg.reduce((a, b) => a + b)
  return Array.from({ length: n }, (_, k) => {
    let d = (k / (n - 1)) * len, i = 0
    while (i < seg.length - 1 && d > seg[i]) d -= seg[i++]
    const u = d / seg[i]
    return [pts[i][0] + (pts[i + 1][0] - pts[i][0]) * u, pts[i][1] + (pts[i + 1][1] - pts[i][1]) * u]
  })
}
const T_CENTRE = (n, d = 0) => Array.from({ length: n }, () => [0, 0, d])
const T_SHAPES = {
  heap: [[0.02, 0, 0.32], [-0.2, -0.12, 0.2], [0.2, 0.1, 0.22], [-0.14, 0.18, 0.18], [0.16, -0.16, 0.16], [-0.3, 0.04, 0.12], [0.3, -0.04, 0.1], [0.04, 0.26, 0.12], [-0.04, -0.27, 0.11], [0.26, 0.24, 0.08], [-0.26, -0.24, 0.08], [0.06, -0.06, 0.2], [-0.08, 0.06, 0.2], [0.36, 0.14, 0.06], [-0.36, 0.2, 0.06], [0.14, 0.34, 0.05], [-0.18, -0.33, 0.05], [0, 0, 0.2], [0.1, 0.08, 0.16], [-0.1, -0.08, 0.16], [0.22, -0.26, 0.07], [-0.24, 0.3, 0.07]],
  // slim soft frame (6 + 6 + 2) + squared-off nub (2), three lavender bars of two dots inside
  battery: [...[-0.38, -0.228, -0.076, 0.076, 0.228, 0.38].flatMap((x) => [[x, -0.2, 0.17], [x, 0.2, 0.17]]), [-0.38, 0, 0.17], [0.38, 0, 0.17], [0.5, -0.04, 0.11], [0.5, 0.04, 0.11], ...[-0.15, 0, 0.15].flatMap((x) => [[x, -0.025, 0.1], [x, 0.025, 0.1]])],
  // portrait screen of six big blobs; the rest hide in it (one becomes the tap)
  display: [...[-0.17, 0, 0.17].flatMap((y) => [[-0.08, y, 0.3], [0.08, y, 0.3]]), ...T_CENTRE(16, 0.2)],
  // ring of 16, pupil of four, two hidden
  camera: [...Array.from({ length: 16 }, (_, i) => [Math.cos((i / 16) * 2 * Math.PI) * 0.27, Math.sin((i / 16) * 2 * Math.PI) * 0.27, 0.15]), ...T_CENTRE(4, 0.13), ...T_CENTRE(2)],
  // round cluster; the failing dot is pushed out to the side
  reject: [[-0.05, 0, 0.36], ...Array.from({ length: 14 }, (_, i) => [Math.cos(i * 0.45) * 0.1 - 0.05, Math.sin(i * 0.45) * 0.1, 0.2]), [0.32, 0.06, 0.14], ...Array.from({ length: 6 }, () => [-0.05, 0, 0.2])],
  // check mark; the failing dot drops out of the stage on the way
  check: (() => {
    const pts = along([[-0.3, 0], [-0.07, 0.23], [0.34, -0.25]], 21).map(([x, y]) => [x, y, 0.16])
    return [...pts.slice(0, 15), [0.42, 1.3, 0], ...pts.slice(15)] // the failing dot shrinks away as it falls, never lingers below the stage
  })(),
}
// Härtetest = quality/trust: blue (sky) with yellow, no lavender (that is trade-in)
const T_COLORS = {
  heap: () => 'sky',
  battery: (i) => (i < 16 ? 'yellow' : 'sky'),
  display: () => 'sky', // lit up by the tap
  camera: (i) => (i < 16 ? 'yellow' : 'sky'),
  reject: (i) => (i === 15 ? T_GREY : 'yellow'),
  check: (i) => (i === 15 ? T_GREY : 'yellow'),
}
// Story scene (12 blobs): seed → satellites dock → hall → medal → team ring around a photo
const S_RING = (n, r, d) => Array.from({ length: n }, (_, i) => [Math.cos((i / n) * 2 * Math.PI - Math.PI / 2) * r, Math.sin((i / n) * 2 * Math.PI - Math.PI / 2) * r, d])
Object.assign(T_SHAPES, {
  seed: [[0, 0, 0.18], ...T_CENTRE(11)],
  // big centre (0, 1, 8–11) with six satellites (2–7) that orbit and dip in
  dock: [[0, 0, 0.3], [0, 0, 0.3], ...S_RING(6, 0.33, 0.1), ...T_CENTRE(4, 0.2)],
  // wide hall with a saw-tooth roof
  hub: [...[-0.24, -0.08, 0.08, 0.24].flatMap((x) => [[x, 0, 0.27], [x, 0.14, 0.27]]), [-0.2, -0.17, 0.17], [0, -0.17, 0.17], [0.2, -0.17, 0.17], [0, 0.07, 0.2]],
  // disc (0–4, 11) on two ribbon tails (5–10)
  medal: [[0, -0.1, 0.36], [-0.08, -0.1, 0.3], [0.08, -0.1, 0.3], [0, -0.18, 0.3], [0, -0.02, 0.3], [-0.07, 0.15, 0.11], [-0.11, 0.25, 0.11], [-0.15, 0.35, 0.11], [0.07, 0.15, 0.11], [0.11, 0.25, 0.11], [0.15, 0.35, 0.11], [0, -0.1, 0.3]],
  // ring of small blobs (the team) around the photo
  team: S_RING(12, 0.37, 0.12),
})
Object.assign(T_COLORS, {
  seed: () => 'lavender',
  dock: (i) => (i >= 2 && i <= 7 ? 'yellow' : 'lavender'),
  hub: () => 'yellow',
  medal: (i) => (i >= 5 && i <= 10 ? 'lavender' : 'yellow'),
  team: (i) => (i % 2 ? 'lavender' : 'yellow'),
})
// Wirkung scene (22 blobs): a phone, then one shape per figure
const W_ROW = (y, xs, d) => xs.map((x) => [x, y, d])
Object.assign(T_SHAPES, {
  phone: T_SHAPES.display,
  cloud: [[-0.22, 0.08, 0.24], [-0.07, 0.1, 0.26], [0.08, 0.1, 0.26], [0.23, 0.08, 0.24], [-0.14, -0.06, 0.28], [0.06, -0.12, 0.34], [0.22, -0.03, 0.22], ...Array.from({ length: 15 }, () => [0, 0.03, 0.26])],
  // a pile of small pieces, 6 / 5 / 4 / 3 / 2 / 1
  pile: [...W_ROW(0.26, [-0.3, -0.18, -0.06, 0.06, 0.18, 0.3], 0.13), ...W_ROW(0.15, [-0.24, -0.12, 0, 0.12, 0.24], 0.13), ...W_ROW(0.04, [-0.18, -0.06, 0.06, 0.18], 0.13), ...W_ROW(-0.07, [-0.12, 0, 0.12], 0.13), ...W_ROW(-0.18, [-0.06, 0.06], 0.13), [0, -0.29, 0.13], [0, 0.1, 0.12]],
  drop: [[0, 0.1, 0.44], [0, -0.06, 0.32], [0, -0.17, 0.22], [0, -0.25, 0.14], [0, -0.31, 0.08], ...Array.from({ length: 17 }, () => [0, 0.1, 0.36])],
})
Object.assign(T_COLORS, {
  phone: () => 'yellow',
  cloud: () => 'lavender',
  pile: (i) => (i % 3 ? 'lavender' : T_GREY),
  drop: () => 'lavender',
})
// dots an effect places itself while the shape is held
const T_OWN = { display: (i) => i === 21, dock: (i) => i >= 2 && i <= 7, medal: () => true, team: () => true }
const TAPS = [[0.06, 0.1], [-0.08, -0.17], [0.1, -0.2], [-0.06, 0.2]]
const morph = (keys, t, i) => {
  for (let k = keys.length - 2; k >= 0; k--) {
    const [t0, a] = keys[k]
    const [t1, b] = keys[k + 1]
    if (t >= t0) return a[i].map((v, j) => v + (b[i][j] - v) * ease(clamp((t - t0) / (t1 - t0))))
  }
  return keys[0][1][i]
}
for (const trun of document.querySelectorAll('[data-trun]')) {
  const dots = [...trun.querySelectorAll('.trun__dot')]
  const chs = [...trun.querySelectorAll('[data-ch]')]
  const imgs = [...trun.querySelectorAll('[data-img-for]')]
  const count = trun.querySelector('[data-trun-count]')
  const story = trun.dataset.scene !== 'test' // story and impact show the chapter's marker instead of the counter
  const total = Number(trun.dataset.total) || 40
  const names = chs.map((c, i) => (T_SHAPES[c.dataset.shape]?.length === dots.length ? c.dataset.shape : i ? 'check' : 'heap'))
  const shapes = names.map((n) => T_SHAPES[n])
  // beat k = chapter k: hold its shape, then morph to the next one
  const keys = shapes.flatMap((sh, k) => [[k, sh], [k + 0.55, sh]])
  const last = shapes.length - 1
  const place = (d, [x, y, w]) => Object.assign(d.style, { left: `${50 + x * 100}%`, top: `${50 + y * 100}%`, width: `${w * 100}%` })
  const colors = []
  const paint = (i, c) => c !== colors[i] && (dots[i].dataset.blobColor = colors[i] = c)
  const scales = []
  const size = (i, v) => v !== scales[i] && (dots[i].dataset.blobScale = scales[i] = v)
  let active = -1
  let since = Infinity // when the active shape was fully formed (time-based effects start then)
  let held = false
  const update = () => {
    const r = trun.getBoundingClientRect()
    const T = clamp(-r.top / (r.height - innerHeight)) * (last + 0.55)
    if (!story) {
      const p = clamp((T - 0.55) / (last - 0.55))
      count.textContent = p >= 1 ? `${total}+` : String(Math.round(p * total))
    }
    const a = Math.min(last, Math.floor(T + 0.45))
    if (a !== active) {
      chs.forEach((c, i) => (c.classList.toggle('is-on', i === a), c.classList.toggle('is-past', i < a)))
      imgs.forEach((im) => im.classList.toggle('is-on', Number(im.dataset.imgFor) === a))
      dots.forEach((_, i) => (paint(i, T_COLORS[names[a]](i)), size(i, 1)))
      if (story && chs[a].dataset.marker) count.textContent = chs[a].dataset.marker
      active = a
      since = Infinity
    }
    held = T - Math.floor(T) < 0.6 && Math.floor(T) === a
    if (held && since === Infinity) since = performance.now()
    if (!held) since = Infinity
    const own = held && T_OWN[names[a]]
    dots.forEach((d, i) => (own && own(i)) || place(d, morph(keys, T - i * 0.008, i)))
  }

  // time-based life inside a formed shape
  const fx = (now) => {
    const name = names[active]
    const t = now - since
    if (!(t > 0)) return name === 'display' && size(21, 0)
    if (name === 'battery') {
      const level = Math.floor(t / 420) % 6 // 0–3 bars, then hold full
      for (let b = 0; b < 3; b++) size(16 + b * 2, b < level ? 1 : 0), size(17 + b * 2, b < level ? 1 : 0)
    } else if (name === 'display') {
      for (let i = 16; i < 21; i++) size(i, 0)
      const dt = (t - 700) % 2200
      const k = Math.floor((t - 700) / 2200)
      if (t < 700) return size(21, 0)
      const [tx, ty] = TAPS[k % TAPS.length]
      place(dots[21], [tx, ty, 0.1])
      paint(21, '#272525') // fingertip
      size(21, dt < 650 ? 1 : 0)
      // a wave runs out from the finger: dots swell as it passes and light up yellow
      for (let i = 0; i < 16; i++) {
        const [x, y] = T_SHAPES.display[i]
        const w = dt - 120 - Math.hypot(x - tx, y - ty) * 1300
        size(i, w > 0 && w < 200 ? 1.1 : 1)
        if (k > 0 || w > 0) paint(i, 'yellow')
      }
    } else if (name === 'camera') {
      const f = 1 + 0.3 * Math.sin(t / 420) ** 2
      for (let i = 16; i < 20; i++) size(i, f)
    } else if (name === 'dock') {
      // satellites orbit and now and then dip into the centre
      for (let i = 2; i <= 7; i++) {
        const ang = ((i - 2) / 6) * 2 * Math.PI - Math.PI / 2 + t / 2600
        const r = 0.33 - 0.19 * Math.max(0, Math.sin(t / 750 + i * 1.7)) ** 6
        place(dots[i], [Math.cos(ang) * r, Math.sin(ang) * r, 0.1])
      }
    } else if (name === 'medal') {
      // swings gently from the top of the ribbon
      const ang = 0.08 * Math.sin(t / 800)
      const [c, sn] = [Math.cos(ang), Math.sin(ang)]
      T_SHAPES.medal.forEach(([x, y, w], i) => place(dots[i], [x * c - (y + 0.45) * sn, x * sn + (y + 0.45) * c - 0.45, w]))
    } else if (name === 'team') {
      // the ring turns slowly, every blob bobs a little
      T_SHAPES.team.forEach((_, i) => {
        const ang = (i / 12) * 2 * Math.PI - Math.PI / 2 + t / 6000
        const r = 0.37 + 0.02 * Math.sin(t / 500 + i)
        place(dots[i], [Math.cos(ang) * r, Math.sin(ang) * r, 0.12])
      })
    }
  }
  let raf = 0
  const loop = (now) => {
    fx(now)
    raf = requestAnimationFrame(loop)
  }

  if (reduced) {
    dots.forEach((d, i) => ((d.dataset.blobColor = T_COLORS[names[last]](i)), place(d, shapes[last][i])))
    imgs.forEach((im) => im.classList.toggle('is-on', Number(im.dataset.imgFor) === last))
    count.textContent = story ? chs[last].dataset.marker : `${total}+`
  } else {
    onScroll.push(update)
    update()
    new IntersectionObserver(([e]) => (cancelAnimationFrame(raf), e.isIntersecting && (raf = requestAnimationFrame(loop)))).observe(trun)
  }
}

// ---------- Kreislauf (image-text): step blobs turn in a circle, one after the other grows and turns yellow ----------
for (const cyc of document.querySelectorAll('[data-cycle]')) {
  const nodes = [...cyc.querySelectorAll('.cyc__node')]
  const dot = cyc.querySelector('.cyc__dot')
  const n = nodes.length
  const STEP = 1900 // ms per step
  const at = (el, ang, r) => Object.assign(el.style, { left: `${50 + Math.cos(ang) * r}%`, top: `${50 + Math.sin(ang) * r}%` })
  let lit = -1
  const frame = (now) => {
    const spin = reduced ? 0 : now / 14000
    const k = reduced ? 0 : Math.floor(now / STEP) % n
    const u = reduced ? 0 : (now % STEP) / STEP
    nodes.forEach((el, i) => at(el, -Math.PI / 2 + (i / n) * 2 * Math.PI + spin, 31))
    if (k !== lit) {
      nodes.forEach((el, i) => ((el.dataset.blobColor = i === k ? 'yellow' : 'lavender'), (el.dataset.blobScale = i === k ? 1.22 : 0.9)))
      lit = k
    }
    // the small blob leaves the lit step and travels along the circle to the next one
    const t = clamp((u - 0.45) / 0.5)
    at(dot, -Math.PI / 2 + ((k + ease(t)) / n) * 2 * Math.PI + spin, 31)
    dot.dataset.blobScale = t > 0 && t < 1 ? 1 : 0
  }
  let raf = 0
  const loop = (now) => (frame(now), (raf = requestAnimationFrame(loop)))
  frame(0)
  if (!reduced) new IntersectionObserver(([e]) => (cancelAnimationFrame(raf), e.isIntersecting && (raf = requestAnimationFrame(loop)))).observe(cyc)
}

// ---------- Process steps: the step in the middle of the screen lights up, the sticky stage follows ----------
for (const sec of document.querySelectorAll('[data-steps]')) {
  const steps = [...sec.querySelectorAll('[data-step]')]
  const pics = [...sec.querySelectorAll('[data-step-pic]')]
  const count = sec.querySelector('[data-step-count]')
  const blob = sec.querySelector('.steps__blob')
  const show = (k) => {
    steps.forEach((el, i) => el.classList.toggle('is-on', i === k))
    pics.forEach((el) => el.classList.toggle('is-on', Number(el.dataset.stepPic) === k))
    if (count) count.textContent = String(k + 1).padStart(2, '0')
    if (blob) blob.dataset.blobColor = k % 2 ? 'sky' : 'yellow' // refurbishing steps = trust: blue
  }
  show(0)
  const io = new IntersectionObserver((es) => es.forEach((e) => e.isIntersecting && show(Number(e.target.dataset.step))), { rootMargin: '-45% 0px -45% 0px' })
  steps.forEach((el) => io.observe(el))
}

// ---------- Numbers count up when scrolled to (promise; milestones: data-countup="1,5" keeps the decimals) ----------
for (const n of document.querySelectorAll('.promise [data-count], [data-countup]')) {
  if (reduced) continue
  const raw = n.dataset.countup ?? n.dataset.count
  const to = Number(raw.replace(',', '.'))
  const dec = raw.split(',')[1]?.length || 0
  const fmt = (v) => (dec ? v.toFixed(dec).replace('.', ',') : String(Math.round(v)))
  n.textContent = fmt(0)
  new IntersectionObserver(([e], o) => {
    if (!e.isIntersecting) return
    o.disconnect()
    const start = performance.now()
    const step = (now) => {
      const u = clamp((now - start) / 1600)
      n.textContent = fmt(to * (1 - (1 - u) ** 4))
      if (u < 1) requestAnimationFrame(step)
    }
    requestAnimationFrame(step)
  }, { rootMargin: '0px 0px -15% 0px' }).observe(n)
}

// ---------- Wunschgerät-Finder ----------
const finder = document.querySelector('[data-finder]')
if (finder) {
  const qs = [...finder.querySelectorAll('[data-q]')]
  const rail = [...finder.querySelectorAll('.finder__rail li')]
  const next = finder.querySelector('[data-next]')
  const back = finder.querySelector('[data-back]')
  const count = finder.querySelector('[data-finder-count]')
  const out = finder.querySelector('[data-out]')
  const grid = finder.querySelector('[data-grid]')
  const all = finder.querySelector('[data-all]')
  const showBtn = finder.querySelector('[data-show]') // pop-up only: "14 Geräte anzeigen" → the filtered list
  const STORAGE = ['128 GB', '256 GB', '512 GB', '1 TB']
  let step = 0
  let ctrl = null
  let osMap = {} // label → filter value, from the results section (the shop groups OS values under gid:// ids)

  const values = (q) => [...q.querySelectorAll('[aria-pressed="true"]')].map((b) => b.dataset.v).filter(Boolean)
  const url = () => {
    const p = new URLSearchParams()
    const [price] = values(qs[0])
    if (price) {
      const [lo, hi] = price.split('-')
      if (lo) p.set('filter.v.price.gte', lo)
      if (hi) p.set('filter.v.price.lte', hi)
    }
    const [os] = values(qs[1])
    if (os) p.append('filter.v.m.global.Betriebssystem', osMap[os.toLowerCase()] || os)
    const [min] = values(qs[2])
    if (min) STORAGE.slice(STORAGE.indexOf(min)).forEach((s) => p.append('filter.v.m.global.Speicher', s)) // "at least"
    values(qs[3]).forEach((c) => p.append('filter.v.m.global.Farbe', c))
    p.set('filter.v.availability', '1')
    return `${finder.dataset.url}?${p}`
  }
  const refresh = async () => {
    ctrl?.abort()
    ctrl = new AbortController()
    const target = url()
    try {
      const text = await fetch(`${target}&section_id=finder-results`, { signal: ctrl.signal }).then((r) => r.text())
      const res = new DOMParser().parseFromString(text, 'text/html').querySelector('[data-finder-results]')
      try {
        osMap = { ...osMap, ...JSON.parse(res?.dataset.os || '{}') }
      } catch {}
      const n = Number(res?.dataset.count || 0)
      count.innerHTML = `<b>${n}</b>${n === 1 ? finder.dataset.one || 'Gerät passt' : finder.dataset.many || 'Geräte passen'}`
      grid.replaceChildren(...(res ? res.querySelectorAll(':scope > li') : []))
      all.href = target
      all.hidden = n <= grid.children.length
      if (showBtn) {
        showBtn.href = target
        showBtn.firstChild.textContent = n === 1 ? showBtn.dataset.one : n ? showBtn.dataset.label.replace('#', n) : showBtn.dataset.none
        showBtn.setAttribute('aria-disabled', String(!n))
      }
      out.hidden = n === 0
    } catch (e) {
      if (e.name !== 'AbortError') count.textContent = ''
    }
  }
  const show = (i) => {
    step = i
    qs.forEach((q, j) => q.classList.toggle('is-on', j === i))
    rail.forEach((li, j) => li.classList.toggle('is-on', j <= i))
    back.hidden = i === 0
    next.hidden = i === qs.length - 1
  }
  finder.addEventListener('click', (e) => {
    const b = e.target.closest('[data-q] [data-v]')
    if (b) {
      const q = b.closest('[data-q]')
      const multi = q.hasAttribute('data-multi')
      const anyBtn = q.querySelector('[data-v=""]')
      if (!multi || !b.dataset.v) q.querySelectorAll('[data-v]').forEach((x) => x.setAttribute('aria-pressed', String(x === b)))
      else {
        b.setAttribute('aria-pressed', String(b.getAttribute('aria-pressed') !== 'true'))
        anyBtn.setAttribute('aria-pressed', String(!values(q).length))
      }
      refresh()
      if (!multi && step < qs.length - 1) setTimeout(() => show(step + 1), 280) // single choice: on to the next question
      return
    }
    if (e.target.closest('[data-next]')) show(Math.min(step + 1, qs.length - 1))
    if (e.target.closest('[data-back]')) show(Math.max(step - 1, 0))
  })
  // first count once the finder comes near (or its pop-up opens), not on page load
  new IntersectionObserver(([e], o) => e.isIntersecting && (o.disconnect(), refresh()), { rootMargin: '400px' }).observe(finder)

  // as a pop-up (#FinderPop): opened by any [data-finder-open], e.g. the collection's finder tile
  const pop = finder.closest('#FinderPop')
  if (pop) {
    const reduced = document.documentElement.classList.contains('reduced')
    const close = () => {
      if (!pop.open) return
      if (reduced) return pop.close()
      pop.animate([{ opacity: 1, scale: 1 }, { opacity: 0, scale: 0.96 }], { duration: 250, easing: 'ease-in' }).onfinish = () => pop.close()
    }
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-finder-open]')) {
        e.preventDefault()
        pop.showModal()
        window.janado?.lenis?.stop()
        if (!reduced) pop.animate([{ opacity: 0, scale: 0.96 }, { opacity: 1, scale: 1 }], { duration: 500, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' })
      } else if (e.target === pop || e.target.closest('[data-finder-close]')) close()
    })
    pop.addEventListener('cancel', (e) => (e.preventDefault(), close()))
    pop.addEventListener('close', () => window.janado?.lenis?.start())
  }
}
